import { Button, TextInput } from '@neo4j-ndl/react';
import { ArrowLongLeftIconOutline } from '@neo4j-ndl/react/icons';
import { Objects, isNonEmptyString } from '@nx/stdlib';

import type { OnboardingData } from '../entities/model';
import { ONBOARDING_INTENT, intentLabel } from '../entities/model';
import { OnboardingItemButton } from '../onboarding-button';
import { BaseSection } from './base-section';

type IntentSectionProps = {
  onNextStep: (intent: ONBOARDING_INTENT, intentOther?: string) => void;
  onDataChange: (data: Partial<OnboardingData>) => void;
  onBack: () => void;
  isLoading?: boolean;
  data: OnboardingData;
};

export const IntentSection = ({ onNextStep, onBack, data, isLoading = false, onDataChange }: IntentSectionProps) => {
  const handleSelectIntent = (intent: ONBOARDING_INTENT, other?: string | null) => {
    if (data.onboardingIntent !== intent) {
      onNextStep(intent, other ?? undefined);
    }
  };

  const handleNextClick = (intent: ONBOARDING_INTENT, other?: string | null) => {
    if (intent === ONBOARDING_INTENT.Other && isNonEmptyString(other)) {
      onNextStep(intent, other);
    }
  };

  return (
    <BaseSection title="What are you looking to achieve with Neo4j?" step={1}>
      <div className="flex grow flex-col justify-between gap-6">
        <div className="flex flex-col gap-4">
          {Objects.keys(intentLabel).map((key) => (
            <OnboardingItemButton
              key={key}
              onClick={() => handleSelectIntent(key)}
              size="large"
              isDisabled={isLoading}
              isLoading={key === data.onboardingIntent && key !== ONBOARDING_INTENT.Other && isLoading}
              isActive={key === data.onboardingIntent && !isLoading}
            >
              {intentLabel[key]}
            </OnboardingItemButton>
          ))}
          {data.onboardingIntent === ONBOARDING_INTENT.Other && (
            <TextInput
              size="large"
              value={data.onboardingIntentOther ?? ''}
              onChange={(event) => onDataChange({ onboardingIntentOther: event.target.value })}
              isFluid
              isDisabled={isLoading}
              htmlAttributes={{
                placeholder: "Describe what you're looking to achieve...",
                'aria-label': 'Other',
              }}
            />
          )}
        </div>

        <div className="flex w-full justify-between self-start">
          <Button
            size="large"
            onClick={onBack}
            color="neutral"
            fill="outlined"
            className="text-neutral-text-default hover:bg-neutral-hover active:bg-palette-neutral-pressed flex gap-2 !border-none"
          >
            <ArrowLongLeftIconOutline className="size-5" />
            Back
          </Button>
          {data.onboardingIntent === ONBOARDING_INTENT.Other && (
            <Button
              size="large"
              type="submit"
              onClick={() => handleNextClick(ONBOARDING_INTENT.Other, data.onboardingIntentOther)}
              isLoading={isLoading}
              isDisabled={!isNonEmptyString(data.onboardingIntentOther)}
            >
              Next
            </Button>
          )}
        </div>
      </div>
    </BaseSection>
  );
};
